import * as React from 'react'
import './generate-test-domain-modal.scss'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { DomainDto } from '@halo/dtos/domain.dto'
import { InfoCircleFilled, InfoCircleOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Modal, Input, Select, Skeleton } from 'antd'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { DomainService } from '@halo/services/domain.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import ColorPickerPopover from '@halo/components/color-picker-popover/color-picker-popover'
import TestDomainOverlay from '@halo/components/test-domain-overlay/test-domain-overlay'
import { legacyValidateFields } from '@halo/_utils/antd'
import { simpleFormErrorNotification } from '@halo/_utils/notifications'

interface IGenerateTestDomainModalProps extends FormComponentProps {
    domain: DomainDto
    visible?: boolean
    loading?: boolean
    onCancel: () => void
    onOk: (domain: DomainDto) => any
}

interface IState {
    loading: boolean
    testDomains: DomainDto[]
    color: string
    prefix: string
    name: string
}

const GenerateTestDomainModal = Form.create<IGenerateTestDomainModalProps>()(
    class extends React.Component<IGenerateTestDomainModalProps, IState> {
        public state: IState = {
            loading: false,
            testDomains: [],
            color: '#ffbf00',
            prefix: 'TEST',
            name: '',
        }

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService

        public constructor(props: IGenerateTestDomainModalProps) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)
        }

        public render() {
            const { loading, visible, domain } = this.props

            let namePlaceholder = this.props.domain?.name
            if (namePlaceholder) {
                namePlaceholder = `test.${namePlaceholder}`
            }

            return (
                <Modal
                    className={classnames('generate-test-confirm')}
                    title="Generate Test Domain"
                    visible={visible}
                    confirmLoading={this.state.loading}
                    onOk={this.handleOk}
                    onCancel={this.handleClose}
                >
                    <Skeleton loading={loading} active={true} title={false}>
                        <p>
                            Are you sure you want to generate a test domain for <b>{domain.display_name}</b>?
                        </p>

                        <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} className="hide-inline-errors">
                            <div className="generate-test-confirm">
                                <Form.Item label="Prefix">
                                    <div className="generate-test-confirm-group">
                                        <div className={classnames('form-layout-row')}>
                                            {this.props.form.getFieldDecorator('prefix', {
                                                initialValue: 'TEST',
                                            })(
                                                <Input.Group
                                                    compact={true}
                                                    size="small"
                                                    className="generate-test-confirm-input"
                                                >
                                                    <Select
                                                        size="small"
                                                        defaultValue={this.state.prefix}
                                                        onSelect={(value: string) => {
                                                            this.setState({ prefix: value })
                                                            this.props.form.setFieldsValue({ prefix: value })
                                                        }}
                                                    >
                                                        <Select.Option value="TEST">Test</Select.Option>
                                                        <Select.Option value="DEV">Dev</Select.Option>
                                                        <Select.Option value="QA">QA</Select.Option>
                                                    </Select>
                                                    <Input value={domain.display_name} disabled={true} />
                                                </Input.Group>,
                                            )}
                                            <div className={classnames('form-layout-row-sub')}>
                                                <InfoCircleOutlined />
                                                <span>
                                                    Display Name will appear as ({this.state.prefix}){' '}
                                                    {domain.display_name}.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Form.Item>

                                <Form.Item label="Domain" className="generate-test-confirm-label">
                                    {this.props.form.getFieldDecorator('name', {
                                        rules: [
                                            {
                                                required: true,
                                                message: 'Domain is required.',
                                            },
                                        ],
                                    })(
                                        <Input
                                            size="small"
                                            placeholder={namePlaceholder}
                                            onChange={(e) => {
                                                this.setState({ name: e.target.value })
                                            }}
                                        />,
                                    )}
                                </Form.Item>

                                <Form.Item label="Domain Icon Color" className="generate-test-confirm-label">
                                    <div className="generate-test-confirm-group">
                                        <div className="color-selections">
                                            {this.props.form.getFieldDecorator('overlayColor')(
                                                <ColorPickerPopover
                                                    defaultColor={this.state.color}
                                                    color={this.state.color}
                                                    onChange={(color) => this.setState({ color })}
                                                />,
                                            )}
                                        </div>

                                        <TestDomainOverlay
                                            label={this.state.prefix}
                                            color={this.state.color}
                                            icon={domain.default_icon_url}
                                        />
                                    </div>
                                </Form.Item>
                            </div>
                        </Form>
                    </Skeleton>
                </Modal>
            )
        }

        protected handleOk = async () => {
            this.setState({ loading: true })

            try {
                await legacyValidateFields(this.props.form)
                const form = this.props.form.getFieldsValue()

                const prefix = form.prefix?.trim()
                let color = form.overlayColor
                const name = form.name?.trim()

                if (!prefix) {
                    form.prefix = 'TEST'
                }

                if (!color) {
                    color = '#ffbf00'
                }

                const testDto = {
                    account_id: this.props.domain.account_id,
                    prod_domain_id: this.props.domain.id,
                    name,
                    display_meta: {
                        prefix,
                        overlay_config: {
                            color,
                        },
                    },
                }

                const res = await this.domainService.create(testDto, {
                    showLoadingScreen: true,
                })

                if (res.ok) {
                    this.props.onOk(res.data)
                    this.handleClose()
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }

            this.setState({ loading: false })
        }

        protected handleClose = () => {
            this.props.form.resetFields()
            this.props.onCancel()
        }
    },
)

export default GenerateTestDomainModal
